import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Assignment {
  id: number
  case_id: number
  case_number: string | null
  installed_at: string | null
  installed_by: string | null
  install_vehicle: string | null
  install_gps_location: string | null
  install_vehicle_location: string | null
  removed_at: string | null
  removed_by: string | null
  remove_vehicle_location: string | null
  returned_at: string | null
  returned_by: string | null
  status: number
  created_at: string
}

interface GpsDevice {
  id: number
  name: string
}

interface Props {
  gps_device: GpsDevice
  assignments: Assignment[]
}

export default function Index({ gps_device, assignments }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "GPS Devices", href: "/gps_devices" },
    { title: gps_device.name, href: `/gps_devices/${gps_device.id}` },
    { title: "Assignments", href: `/gps_devices/${gps_device.id}/assignments` },
  ]

  const formatDateTime = (value: string | null) => {
    if (!value) return "-"
    return new Date(value).toLocaleString()
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${gps_device.name} - Assignments`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {gps_device.name} - Case Assignments
          </h1>
          <Link
            href={`/gps_devices/${gps_device.id}`}
            className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
          >
            Back to Device
          </Link>
        </div>

        {assignments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="border-hpi-light-gray w-full border-collapse border">
              <thead>
                <tr className="bg-hpi-pale-gray">
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Case
                  </th>
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Installed
                  </th>
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Vehicle
                  </th>
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Install Location
                  </th>
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Removed
                  </th>
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Returned
                  </th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-hpi-pale-blue">
                    <td className="border-hpi-light-gray border px-3 py-2">
                      {assignment.case_id ? (
                        <Link
                          href={`/cases/${assignment.case_id}`}
                          className="text-hpi-active-blue hover:text-hpi-active-blue"
                        >
                          {assignment.case_number ?? `#${assignment.case_id}`}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2">
                      <div>{formatDateTime(assignment.installed_at)}</div>
                      {assignment.installed_by && (
                        <div className="text-hpi-med-gray text-sm">
                          by {assignment.installed_by}
                        </div>
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2">
                      {assignment.install_vehicle ?? "-"}
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2">
                      {assignment.install_vehicle_location && (
                        <div>{assignment.install_vehicle_location}</div>
                      )}
                      {assignment.install_gps_location && (
                        <div className="text-hpi-med-gray text-sm">
                          {assignment.install_gps_location}
                        </div>
                      )}
                      {!assignment.install_vehicle_location &&
                        !assignment.install_gps_location &&
                        "-"}
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2">
                      <div>{formatDateTime(assignment.removed_at)}</div>
                      {assignment.removed_by && (
                        <div className="text-hpi-med-gray text-sm">
                          by {assignment.removed_by}
                        </div>
                      )}
                      {assignment.remove_vehicle_location && (
                        <div className="text-hpi-med-gray text-sm">
                          at {assignment.remove_vehicle_location}
                        </div>
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2">
                      <div>{formatDateTime(assignment.returned_at)}</div>
                      {assignment.returned_by && (
                        <div className="text-hpi-med-gray text-sm">
                          by {assignment.returned_by}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-hpi-med-gray">No assignments found.</p>
        )}
      </div>
    </AppLayout>
  )
}
